import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import HistoryItem, { HistoryItemSkeleton } from './HistoryItem';
import { History } from '@features/siteStatusV2/types';
import { Icon } from '../../icon';
import theme from '@theme';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '../../../../../locales/constants/translationNamespaces.enum';

interface HistoriesProps {
  histories: History[] | undefined;
  isHistoryLoading: boolean;
  error: string | undefined;
}

const Histories: React.FC<HistoriesProps> = ({ histories, isHistoryLoading, error }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const maxNumberHistoryDisplay = 2;
  const [isHistoriesHide, setIsHistoriesHide] = useState<boolean>(true);
  const historyDisplay = useMemo(() => {
    if (histories && histories.length > maxNumberHistoryDisplay && isHistoriesHide) {
      return histories.slice(0, maxNumberHistoryDisplay);
    }
    return histories;
  }, [histories, isHistoriesHide]);

  if (isHistoryLoading && !error) {
    return (
      <HistoriesContainer>
        <HistoryItemSkeleton />
        <HistoryItemSkeleton />
      </HistoriesContainer>
    );
  }
  if (historyDisplay && historyDisplay.length > 0 && !error) {
    return (
      <HistoriesContainer>
        {historyDisplay.map((history) => (
          <HistoryItem
            key={history.id}
            name={history.creator}
            creationDate={history.created}
            informations={history.content}
            attachments={history.attachments}
          />
        ))}
        <ViewMoreButton onClick={() => setIsHistoriesHide((value) => !value)}>
          {isHistoriesHide ? t('report.histories.viewMore') : t('report.histories.viewLess')}
        </ViewMoreButton>
      </HistoriesContainer>
    );
  }

  if (historyDisplay && historyDisplay.length === 0 && !error) {
    return (
      <StateContainer>
        <CustomStateIcon iconName="empty-data" />
        <StateMessage>{t('report.histories.emptyData')}</StateMessage>
      </StateContainer>
    );
  }
  return (
    <StateContainer>
      <CustomStateIcon iconName="error-state" />
      <StateMessage>{t('report.histories.errorFetch')}</StateMessage>
    </StateContainer>
  );
};

const HistoriesContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: clip;
  padding-bottom: 0.75rem;
  padding-right: 0.5rem;
`;

const StateContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const CustomStateIcon = styled(Icon)`
  height: 6.5rem;
`;

const StateMessage = styled.div`
  font-weight: 500;
  font-size: 0.75rem;
  line-height: 1rem;
  text-align: center;
  color: ${theme.palette.grey[400]};
`;

const ViewMoreButton = styled.button`
  background-color: ${({ theme }) => theme.palette.grey[100]};
  border-width: 0px;
  width: 100%;
  font-size: 0.75rem;
  line-height: 1.25rem;
  text-align: center;
  color: ${({ theme }) => theme.palette.blue[500]};
  padding-top: 1rem;
  padding-bottom: 1rem;
  &:hover {
    color: ${theme.palette.grey[600]};
  }
`;

export default Histories;
